import { useState } from 'react';
import type { CatalogBundleEntry, CatalogRole, CatalogRoleEntry } from '../../../services/iam';
import { shortRoleKey } from '../iamLookups';
import { AccessCard } from '../primitives/AccessCard';
import { AccessCardSubsection } from '../primitives/AccessCardSubsection';
import { RoleListTabs, type RoleListTab } from './RoleListTabs';

const BUNDLE_THEME_GROUPS_STORAGE_KEY = 'odo:access:bundle-theme-groups';

// Tolerates a missing/corrupt localStorage blob rather than breaking the page.
function loadOpenThemes(): Record<string, boolean> {
    if (typeof globalThis === 'undefined' || !globalThis.localStorage) return {};
    try {
        const raw = globalThis.localStorage.getItem(BUNDLE_THEME_GROUPS_STORAGE_KEY);
        if (!raw) return {};
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed as Record<string, boolean>;
        }
        return {};
    } catch {
        return {};
    }
}

// Bundle keys are `<theme>.<capability>` with no `roles/` prefix - theme is
// derivable straight from the key.
function bundleTheme(key: string): string {
    const dotIndex = key.indexOf('.');
    return dotIndex === -1 ? key : key.slice(0, dotIndex);
}

function themeLabel(theme: string): string {
    return theme.charAt(0).toUpperCase() + theme.slice(1);
}

interface BundleThemeGroup {
    readonly theme: string;
    readonly items: readonly CatalogBundleEntry[];
}

function groupBundlesByTheme(bundles: readonly CatalogBundleEntry[]): BundleThemeGroup[] {
    const groups = new Map<string, CatalogBundleEntry[]>();
    for (const bundle of bundles) {
        const theme = bundleTheme(bundle.key);
        const bucket = groups.get(theme) ?? [];
        bucket.push(bundle);
        groups.set(theme, bucket);
    }
    return Array.from(groups.entries())
        .map(([theme, items]) => ({ theme, items }))
        .sort((a, b) => a.theme.localeCompare(b.theme));
}

// A small card, not a flat row: name plus a two-line-truncated description;
// selection is a light tint (matches PermissionsList's rows) so it marks
// WHICH entry is active without hiding the role/bundle text-treatment distinction.
function RoleListEntry({
    role,
    isSelected,
    onSelect
}: {
    role: CatalogRole;
    isSelected: boolean;
    onSelect: () => void;
}) {
    const isBundle = role.kind === 'bundle';
    return (
        <button
            type="button"
            onClick={onSelect}
            aria-pressed={isSelected}
            className={`w-full text-left rounded-lg border px-3 py-2.5 flex flex-col gap-1 transition-colors ${
                isSelected ? 'bg-gray-100 border-gray-300' : 'border-transparent hover:bg-gray-50'
            }`}
        >
            <span
                className={`text-[12px] font-semibold truncate ${
                    isBundle ? 'font-mono italic text-violet-700' : 'text-gray-800'
                }`}
            >
                {isBundle ? shortRoleKey(role.key) : role.title}
            </span>
            {!isBundle && (
                <span className="text-[10px] font-mono text-gray-400 truncate">
                    {shortRoleKey(role.key)}
                </span>
            )}
            <span className="text-[11px] leading-snug line-clamp-2 text-gray-400">{role.desc}</span>
        </button>
    );
}

interface RolesListProps {
    roles: readonly CatalogRoleEntry[];
    bundles: readonly CatalogBundleEntry[];
    activeTab: RoleListTab;
    onTabChange: (tab: RoleListTab) => void;
    selectedKey: string | null;
    onSelect: (key: string) => void;
}

// Two tabs (roles never mix with bundles - a bundle can't be granted), headerless
// AccessCard since the tab bar is the header. Roles stay flat; bundles group by
// theme (collapsible, fold state persisted), same pattern as PermissionsList.
export function RolesList({
    roles,
    bundles,
    activeTab,
    onTabChange,
    selectedKey,
    onSelect
}: RolesListProps) {
    const [openThemes, setOpenThemes] = useState<Record<string, boolean>>(loadOpenThemes);
    const isThemeOpen = (theme: string) => openThemes[theme] !== false;
    const toggleTheme = (theme: string) => {
        setOpenThemes((prev) => {
            const next = { ...prev, [theme]: !(prev[theme] !== false) };
            try {
                globalThis.localStorage?.setItem(
                    BUNDLE_THEME_GROUPS_STORAGE_KEY,
                    JSON.stringify(next)
                );
            } catch {
                /* ignore */
            }
            return next;
        });
    };

    const bundleGroups = groupBundlesByTheme(bundles);

    return (
        <AccessCard grow noBodyPadding>
            <RoleListTabs
                activeTab={activeTab}
                onChange={onTabChange}
                roleCount={roles.length}
                bundleCount={bundles.length}
            />
            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide flex flex-col">
                {activeTab === 'roles' ? (
                    <div className="flex flex-col gap-1.5 px-2 pt-2 pb-2">
                        {roles.map((role) => (
                            <RoleListEntry
                                key={role.key}
                                role={role}
                                isSelected={selectedKey === role.key}
                                onSelect={() => onSelect(role.key)}
                            />
                        ))}
                    </div>
                ) : (
                    bundleGroups.map((group, index) => (
                        <AccessCardSubsection
                            key={group.theme}
                            label={themeLabel(group.theme)}
                            count={group.items.length}
                            divider={index > 0}
                            collapsible
                            open={isThemeOpen(group.theme)}
                            onToggle={() => toggleTheme(group.theme)}
                        >
                            <div className="flex flex-col gap-1.5 px-2 pb-2">
                                {group.items.map((bundle) => (
                                    <RoleListEntry
                                        key={bundle.key}
                                        role={bundle}
                                        isSelected={selectedKey === bundle.key}
                                        onSelect={() => onSelect(bundle.key)}
                                    />
                                ))}
                            </div>
                        </AccessCardSubsection>
                    ))
                )}
            </div>
        </AccessCard>
    );
}
